import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Select } from 'common-src/components/base';
import Patient, { HasPcg, HasPcgOptions, updatePatient } from 'common-src/models/Patient';
import {
  createPatientCaregiver,
  deletePatientCaregiver,
  getPatientCaregivers,
  getPatientCaregiversRequest,
  updatePatientCaregiver,
  validatePatientCaregiver,
} from 'common-src/models/PatientCaregiver';
import { Colors, ColorsNew } from 'common-src/styles';

import { ModalType } from 'src/components/base/ModalGroup';
import { AddButton, FormButtons } from 'src/components/buttons';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import CaregiverCard from './CaregiverCard';
import CaregiverInlineForm from './CaregiverInlineForm';
import styles from './Caregivers.module.scss';
import { initialState } from './constants';
import {
  catchErrors,
  getInitialFormData,
  getSortedActiveCaregivers,
  getSortedInactiveCaregivers,
} from './helpers';

const Caregivers = ({ patient }) => {
  const dispatch = useDispatch();
  const { showAlert, AlertType } = useAlert();

  const [editingCaregiverId, setEditingCaregiverId] = useState(null);
  const [editingData, setEditingData] = useState(initialState);
  const [editingErrors, setEditingErrors] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [isAdding, setIsAdding] = useState(false);
  const [newCaregiverData, setNewCaregiverData] = useState(initialState);
  const [newCaregiverErrors, setNewCaregiverErrors] = useState({});
  const [isSavingNew, setIsSavingNew] = useState(false);
  const [deletingCaregiverId, setDeletingCaregiverId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [isUpdatingHasPcg, setIsUpdatingHasPcg] = useState(false);

  const patientCaregivers = useCustomSelector((state) => getPatientCaregivers(state, patient.id));

  const caregivers = useMemo(
    () => getSortedActiveCaregivers(patientCaregivers),
    [patientCaregivers],
  );
  const inactiveCaregivers = useMemo(
    () => getSortedInactiveCaregivers(patientCaregivers),
    [patientCaregivers],
  );
  const hasActiveCaregivers = Boolean(caregivers.length);
  const shouldDisableAdd =
    patient.hasPcg === HasPcg.No ||
    (patient.hasPcg === HasPcg.YesOne && hasActiveCaregivers) ||
    isAdding ||
    Boolean(editingCaregiverId);

  useEffect(() => {
    if (!editingCaregiverId) return;

    const caregiver = caregivers.find((item) => item.id === editingCaregiverId);
    if (!caregiver) {
      setEditingCaregiverId(null);
      setEditingData(initialState);
      setEditingErrors({});
    }
  }, [caregivers, editingCaregiverId]);

  const handleRefresh = () => {
    dispatch(getPatientCaregiversRequest({ patientId: patient.id }));
  };

  const handleFieldChange = (setter, errorSetter, data, field, value, forcedError) => {
    const nextData = { ...data, [field]: value };
    const nextErrors =
      typeof forcedError === 'string'
        ? { [field]: forcedError }
        : validatePatientCaregiver(field, value, nextData, patient.isGuide);

    if (field === 'medicareStatus' && value !== 'Yes') {
      errorSetter((prev) => ({ ...prev, mbi: '', ...nextErrors }));
    } else {
      errorSetter((prev) => ({ ...prev, ...nextErrors }));
    }

    setter(nextData);
  };

  const startEditing = (caregiver) => {
    setIsAdding(false);
    setNewCaregiverData(initialState);
    setNewCaregiverErrors({});
    setEditingCaregiverId(caregiver.id);
    setEditingData(getInitialFormData(caregiver, hasActiveCaregivers));
    setEditingErrors({});
  };

  const cancelEditing = () => {
    setEditingCaregiverId(null);
    setEditingData(initialState);
    setEditingErrors({});
  };

  const submitEditing = () => {
    const { errors, hasErrors } = catchErrors(editingData, patient.isGuide);
    if (hasErrors) {
      setEditingErrors(errors);
      return;
    }

    setIsSavingEdit(true);
    dispatch(
      updatePatientCaregiver(
        { id: editingCaregiverId, ...editingData, phone: `+1${editingData.phone}` },
        {
          successBlock: () => {
            setIsSavingEdit(false);
            cancelEditing();
            handleRefresh();
            showAlert(AlertType.Success, '', 'Caregiver updated successfully.');
          },
          errorBlock: (err) => {
            setIsSavingEdit(false);
            showAlert(AlertType.Error, '', err);
          },
        },
      ),
    );
  };

  const startAdding = () => {
    setEditingCaregiverId(null);
    setEditingData(initialState);
    setEditingErrors({});
    setIsAdding(true);
    setNewCaregiverData(getInitialFormData(null, hasActiveCaregivers));
    setNewCaregiverErrors({});
  };

  const cancelAdding = () => {
    setIsAdding(false);
    setNewCaregiverData(initialState);
    setNewCaregiverErrors({});
  };

  const submitAdding = () => {
    const { errors, hasErrors } = catchErrors(newCaregiverData, patient.isGuide);
    if (hasErrors) {
      setNewCaregiverErrors(errors);
      return;
    }

    setIsSavingNew(true);
    dispatch(
      createPatientCaregiver(
        { patientId: patient.id, ...newCaregiverData },
        {
          successBlock: () => {
            setIsSavingNew(false);
            cancelAdding();
            handleRefresh();
            showAlert(AlertType.Success, '', 'Caregiver added successfully.');
          },
          errorBlock: (err) => {
            setIsSavingNew(false);
            showAlert(AlertType.Error, '', err);
          },
        },
      ),
    );
  };

  const confirmMarkInactive = (caregiver) => {
    setDeletingCaregiverId(caregiver.id);
    dispatch(
      deletePatientCaregiver(caregiver.id, {
        successBlock: () => {
          setDeletingCaregiverId(null);
          if (editingCaregiverId === caregiver.id) {
            cancelEditing();
          }
          handleRefresh();
          showAlert(AlertType.Success, '', 'Caregiver marked as inactive.');
        },
        errorBlock: (err) => {
          setDeletingCaregiverId(null);
          showAlert(AlertType.Error, '', err);
        },
      }),
    );
  };

  const handleMarkInactive = (caregiver) => {
    dispatch(
      openModal(ModalType.WARNING, {
        title: 'Remove Entry?',
        message: caregiver.isPrimary
          ? 'Are you sure you want to remove the primary caregiver?\nPlease assign a new primary care giver.'
          : 'Are you sure you want to remove this record?',
        onSubmit: () => confirmMarkInactive(caregiver),
      }),
    );
  };

  return (
    <div className={styles.section}>
      <div className={styles.controlsRow}>
        <div className={styles.controlGroup}>
          <p className={[styles.controlLabel, 'font-s-14', 'font-w-500'].join(' ')}>
            Has Primary Caregiver?
          </p>
          <Select
            id="profile-caregivers-has-pcg-select"
            classNames={[styles.select]}
            placeholder="Select"
            options={HasPcgOptions}
            position="absolute"
            onChange={(option) => {
              setIsUpdatingHasPcg(true);
              dispatch(
                updatePatient(
                  patient.id,
                  { hasPcg: option.value },
                  {
                    successBlock: () => {
                      showAlert(
                        AlertType.Success,
                        '',
                        'You have successfully updated patient pcg status.',
                      );
                      setIsUpdatingHasPcg(false);
                    },
                    errorBlock: (err) => {
                      showAlert(AlertType.Error, '', err);
                      setIsUpdatingHasPcg(false);
                    },
                  },
                ),
              );
            }}
            value={HasPcgOptions.find((op) => op.value === patient.hasPcg)}
            size="small"
            withError={false}
            isLoading={isUpdatingHasPcg}
          />
        </div>
      </div>
      <div className={styles.group}>
        <p className={[styles.groupTitle, 'font-w-600', 'font-s-16'].join(' ')}>
          Active caregivers
        </p>
        {!caregivers.length && !isAdding && (
          <p className={styles.emptyState}>No active caregivers available.</p>
        )}
        {caregivers.map((caregiver) => (
          <CaregiverCard
            key={caregiver.id}
            caregiver={caregiver}
            data={
              editingCaregiverId === caregiver.id
                ? editingData
                : getInitialFormData(caregiver, true)
            }
            errors={editingCaregiverId === caregiver.id ? editingErrors : {}}
            isEditMode={editingCaregiverId === caregiver.id}
            isGuide={patient.isGuide}
            isLoading={editingCaregiverId === caregiver.id && isSavingEdit}
            disableEdit={
              Boolean(editingCaregiverId && editingCaregiverId !== caregiver.id) || isAdding
            }
            isDeleting={deletingCaregiverId === caregiver.id}
            onChange={(field, value, forcedError) =>
              handleFieldChange(
                setEditingData,
                setEditingErrors,
                editingData,
                field,
                value,
                forcedError,
              )
            }
            onEdit={() => startEditing(caregiver)}
            onCancel={cancelEditing}
            onSubmit={submitEditing}
            onMarkInactive={() => handleMarkInactive(caregiver)}
          />
        ))}
      </div>
      <div className={styles.addRow}>
        {isAdding && (
          <div className={styles.caregiverBlock}>
            <div className={styles.caregiverHeader}>
              <p className={[styles.caregiverTitle, 'font-w-600', 'font-s-16'].join(' ')}>
                New caregiver
              </p>
            </div>
            <CaregiverInlineForm
              data={newCaregiverData}
              errors={newCaregiverErrors}
              isEditMode
              isGuide={patient.isGuide}
              onChange={(field, value, forcedError) =>
                handleFieldChange(
                  setNewCaregiverData,
                  setNewCaregiverErrors,
                  newCaregiverData,
                  field,
                  value,
                  forcedError,
                )
              }
            />
            <div className={styles.actionsRow}>
              <FormButtons
                isLoading={isSavingNew}
                isEditMode
                isButtonEnabled
                buttonLabel="caregiver"
                editHandler={() => {}}
                submitHandler={submitAdding}
                cancelHandler={cancelAdding}
                isSectionForm
                sectionName="new-caregiver"
              />
            </div>
          </div>
        )}
        <AddButton
          id="add-caregiver-inline-button"
          label="Add Caregiver"
          onClick={startAdding}
          disabled={shouldDisableAdd}
          classNames={[styles.addButton]}
        />
        {Boolean(inactiveCaregivers.length) && (
          <Button
            id="toggle-inactive-caregivers-button"
            classNames={[styles.toggleButton, 'gap-8']}
            text={showInactive ? 'Hide Inactive' : 'Show Inactive'}
            onClick={() => setShowInactive((prev) => !prev)}
            backgroundColor={Colors.white}
            borderColor="transparent"
            textColor={ColorsNew.darkGreen}
          />
        )}
      </div>
      {Boolean(inactiveCaregivers.length) && showInactive && (
        <div className={styles.group}>
          <p className={[styles.groupTitle, 'font-w-600', 'font-s-16'].join(' ')}>
            Inactive caregivers
          </p>
          {inactiveCaregivers.map((caregiver) => (
            <CaregiverCard
              key={caregiver.id}
              caregiver={caregiver}
              data={getInitialFormData(caregiver, true)}
              errors={{}}
              isGuide={patient.isGuide}
              showActions={false}
              onChange={() => {}}
            />
          ))}
        </div>
      )}
    </div>
  );
};

Caregivers.propTypes = {
  patient: PropTypes.exact(Patient.schema),
};

export default Caregivers;
